/**
 * HHS Poverty Guidelines 2025 — Federal Poverty Level (FPL) data for
 * ACA subsidy calculations.
 *
 * **Source**: U.S. Department of Health and Human Services, "2025
 * Poverty Guidelines for the 48 Contiguous States and DC", "for
 * Alaska", "for Hawaii". Federal Register Vol. 90, January 17, 2025.
 * Retrieved 2026-05-08 from
 * https://aspe.hhs.gov/topics/poverty-economic-mobility/poverty-guidelines
 *
 * **Use**: ACA premium tax credit / subsidy calculation. The 2025 HHS
 * guidelines apply to plan year 2026 (the year for which open
 * enrollment occurred Nov 2025).
 *
 * **Why hardcode**: tiny dataset (~10 rows), updates yearly in
 * January. Live HHS lookup would require scraping a PDF. Phase 11 may
 * add a multi-year selector.
 *
 * **Self-contained types** — file mirrors verbatim from
 * `shared/src/data/` to `web/src/lib/data/`.
 */

export type AcaJurisdiction = "contiguous_48_dc" | "alaska" | "hawaii";

export interface FplBracket {
  /** Household size, 1-8. Sizes >8 use the per-person additive. */
  readonly household_size: number;
  /** 100% FPL annual income, in integer cents. */
  readonly fpl_100_pct_cents: number;
}

export interface FplTable {
  readonly tax_year: number;
  readonly jurisdiction: AcaJurisdiction;
  readonly source: string;
  readonly retrieved_at: string;
  readonly brackets: readonly FplBracket[];
  /** Per-person additive for household sizes > 8. */
  readonly additive_per_person_cents: number;
}

// 48 contiguous states + DC, 2025 HHS Poverty Guidelines.
export const FPL_2025_CONTIGUOUS: FplTable = {
  tax_year: 2025,
  jurisdiction: "contiguous_48_dc",
  source: "HHS 2025 Poverty Guidelines (Fed Reg Jan 17, 2025)",
  retrieved_at: "2026-05-08",
  brackets: [
    { household_size: 1, fpl_100_pct_cents: 15_650_00 },
    { household_size: 2, fpl_100_pct_cents: 21_150_00 },
    { household_size: 3, fpl_100_pct_cents: 26_650_00 },
    { household_size: 4, fpl_100_pct_cents: 32_150_00 },
    { household_size: 5, fpl_100_pct_cents: 37_650_00 },
    { household_size: 6, fpl_100_pct_cents: 43_150_00 },
    { household_size: 7, fpl_100_pct_cents: 48_650_00 },
    { household_size: 8, fpl_100_pct_cents: 54_150_00 },
  ],
  additive_per_person_cents: 5_500_00,
};

export const FPL_2025_ALASKA: FplTable = {
  tax_year: 2025,
  jurisdiction: "alaska",
  source: "HHS 2025 Poverty Guidelines (Alaska)",
  retrieved_at: "2026-05-08",
  brackets: [
    { household_size: 1, fpl_100_pct_cents: 19_550_00 },
    { household_size: 2, fpl_100_pct_cents: 26_430_00 },
    { household_size: 3, fpl_100_pct_cents: 33_310_00 },
    { household_size: 4, fpl_100_pct_cents: 40_190_00 },
    { household_size: 5, fpl_100_pct_cents: 47_070_00 },
    { household_size: 6, fpl_100_pct_cents: 53_950_00 },
    { household_size: 7, fpl_100_pct_cents: 60_830_00 },
    { household_size: 8, fpl_100_pct_cents: 67_710_00 },
  ],
  additive_per_person_cents: 6_880_00,
};

export const FPL_2025_HAWAII: FplTable = {
  tax_year: 2025,
  jurisdiction: "hawaii",
  source: "HHS 2025 Poverty Guidelines (Hawaii)",
  retrieved_at: "2026-05-08",
  brackets: [
    { household_size: 1, fpl_100_pct_cents: 17_990_00 },
    { household_size: 2, fpl_100_pct_cents: 24_320_00 },
    { household_size: 3, fpl_100_pct_cents: 30_650_00 },
    { household_size: 4, fpl_100_pct_cents: 36_980_00 },
    { household_size: 5, fpl_100_pct_cents: 43_310_00 },
    { household_size: 6, fpl_100_pct_cents: 49_640_00 },
    { household_size: 7, fpl_100_pct_cents: 55_970_00 },
    { household_size: 8, fpl_100_pct_cents: 62_300_00 },
  ],
  additive_per_person_cents: 6_330_00,
};

export function fplFor(
  household_size: number,
  jurisdiction: AcaJurisdiction = "contiguous_48_dc",
): number {
  if (!Number.isInteger(household_size) || household_size < 1) {
    throw new Error(
      `fplFor: household_size must be positive integer, got ${household_size}`,
    );
  }
  const table =
    jurisdiction === "alaska"
      ? FPL_2025_ALASKA
      : jurisdiction === "hawaii"
        ? FPL_2025_HAWAII
        : FPL_2025_CONTIGUOUS;
  if (household_size <= 8) {
    const row = table.brackets.find((b) => b.household_size === household_size);
    if (!row) {
      throw new Error(
        `fplFor: missing bracket for household_size=${household_size}`,
      );
    }
    return row.fpl_100_pct_cents;
  }
  // Beyond 8: use the 8-person value + additive × (size - 8).
  const eightPerson = table.brackets[table.brackets.length - 1];
  if (!eightPerson) {
    throw new Error("fplFor: missing 8-person bracket");
  }
  return (
    eightPerson.fpl_100_pct_cents +
    (household_size - 8) * table.additive_per_person_cents
  );
}
